'use client';

import { useState } from 'react';
import type { ReactNode } from 'react';

import type { EmailOtpType } from '@supabase/supabase-js';

import { zodResolver } from '@hookform/resolvers/zod';
import { CheckIcon } from '@radix-ui/react-icons';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { useAppEvents } from '@kit/shared/events';
import { useSignInWithOtp } from '@kit/supabase/hooks/use-sign-in-with-otp';
import { useVerifyOtp } from '@kit/supabase/hooks/use-verify-otp';
import { Alert, AlertDescription, AlertTitle } from '@kit/ui/alert';
import { Button } from '@kit/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@kit/ui/form';
import { If } from '@kit/ui/if';
import { Input } from '@kit/ui/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@kit/ui/input-otp';
import { Trans } from '@kit/ui/trans';

import { useCaptchaToken } from '../captcha/client';
import { AuthErrorAlert } from './auth-error-alert';
import { TermsAndConditionsFormField } from './terms-and-conditions-form-field';

const EMAIL_CODE_LENGTH = 6;
const EMAIL_OTP_TYPE: EmailOtpType = 'email';

const EmailFormSchema = z.object({
  email: z.string().email(),
});

const EmailOtpFormSchema = z.object({
  token: z.string().regex(/^\d{6}$/),
});

type EmailFormValues = z.infer<typeof EmailFormSchema>;
type EmailOtpFormValues = z.infer<typeof EmailOtpFormSchema>;

export function EmailOtpAuthContainer({
  inviteToken,
  redirectUrl,
  shouldCreateUser,
  defaultValues,
  displayTermsCheckbox,
  onVerified,
  passkeyAction,
}: {
  inviteToken?: string;
  redirectUrl: string;
  shouldCreateUser: boolean;
  displayTermsCheckbox?: boolean;
  onVerified?: (userId?: string) => unknown;
  passkeyAction?: ReactNode;

  defaultValues?: {
    email: string;
  };
}) {
  const { captchaToken, resetCaptchaToken } = useCaptchaToken();
  const { t } = useTranslation();
  const signInWithOtpMutation = useSignInWithOtp();
  const verifyOtpMutation = useVerifyOtp();
  const appEvents = useAppEvents();
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [verified, setVerified] = useState(false);

  const emailForm = useForm<EmailFormValues>({
    resolver: zodResolver(EmailFormSchema),
    defaultValues: {
      email: defaultValues?.email ?? '',
    },
  });

  const otpForm = useForm<EmailOtpFormValues>({
    resolver: zodResolver(EmailOtpFormSchema),
    defaultValues: {
      token: '',
    },
  });

  const onSendCode = async ({ email }: EmailFormValues) => {
    const emailRedirectTo = getEmailRedirectTo({
      redirectUrl,
      inviteToken,
    });

    try {
      await signInWithOtpMutation.mutateAsync({
        email,
        options: {
          emailRedirectTo,
          captchaToken,
          shouldCreateUser,
        },
      });

      if (shouldCreateUser) {
        appEvents.emit({
          type: 'user.signedUp',
          payload: {
            method: 'emailOtp',
          },
        });
      }

      setPendingEmail(email);
      otpForm.reset({ token: '' });

      toast.success(t('auth:sendCodeSuccessToast'));
    } catch {
      // Supabase errors are rendered below by AuthErrorAlert.
    } finally {
      resetCaptchaToken();
    }
  };

  const onVerifyCode = async ({ token }: EmailOtpFormValues) => {
    if (!pendingEmail) {
      return;
    }

    try {
      const data = await verifyOtpMutation.mutateAsync({
        email: pendingEmail,
        token,
        type: EMAIL_OTP_TYPE,
      });

      setVerified(true);

      if (onVerified) {
        onVerified(data.user?.id);
      }
    } catch {
      // Supabase errors are rendered below by AuthErrorAlert.
    }
  };

  const loading = signInWithOtpMutation.isPending;
  const verifying = verifyOtpMutation.isPending || verified;
  const error = signInWithOtpMutation.error ?? verifyOtpMutation.error;

  if (verified && !onVerified) {
    return <VerifiedAlert />;
  }

  if (pendingEmail) {
    return (
      <>
        <AuthErrorAlert error={error} />

        <Form {...otpForm}>
          <form
            className={'w-full space-y-4'}
            onSubmit={otpForm.handleSubmit(onVerifyCode)}
          >
            <div className={'text-muted-foreground space-y-1 text-sm'}>
              <Trans
                i18nKey={'auth:emailCodeSentDescription'}
                values={{ email: pendingEmail }}
              />
            </div>

            <FormField
              control={otpForm.control}
              name={'token'}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <Trans i18nKey={'auth:verificationCode'} />
                  </FormLabel>

                  <FormControl>
                    <InputOTP
                      maxLength={EMAIL_CODE_LENGTH}
                      value={field.value}
                      onChange={field.onChange}
                      disabled={verifying}
                      containerClassName={'justify-between'}
                    >
                      <InputOTPGroup>
                        {Array.from({ length: EMAIL_CODE_LENGTH }).map(
                          (_, index) => (
                            <InputOTPSlot key={index} index={index} />
                          ),
                        )}
                      </InputOTPGroup>
                    </InputOTP>
                  </FormControl>

                  <FormDescription>
                    <Trans i18nKey={'auth:emailCodeHint'} />
                  </FormDescription>

                  <FormMessage />
                </FormItem>
              )}
            />

            <div className={'flex flex-col gap-2'}>
              <Button type={'submit'} disabled={verifying}>
                <If
                  condition={verifying}
                  fallback={
                    <>
                      <Trans i18nKey={'auth:verifyEmailCode'} />
                      <ArrowRight className={'h-4'} />
                    </>
                  }
                >
                  <Trans i18nKey={'auth:verifyingEmailCode'} />
                </If>
              </Button>

              <Button
                type={'button'}
                variant={'ghost'}
                disabled={verifying}
                onClick={() => {
                  setPendingEmail(null);
                  verifyOtpMutation.reset();
                }}
              >
                <ArrowLeft className={'h-4'} />
                <Trans i18nKey={'auth:useDifferentEmail'} />
              </Button>
            </div>
          </form>
        </Form>
      </>
    );
  }

  return (
    <>
      <AuthErrorAlert error={error} />

      <Form {...emailForm}>
        <form
          className={'w-full space-y-4'}
          onSubmit={emailForm.handleSubmit(onSendCode)}
        >
          <FormField
            control={emailForm.control}
            name={'email'}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <Trans i18nKey={'common:emailAddress'} />
                </FormLabel>

                <FormControl>
                  <Input
                    data-test={'email-otp-email-input'}
                    required
                    type="email"
                    placeholder={t('auth:emailPlaceholder')}
                    {...field}
                  />
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />

          <If condition={displayTermsCheckbox}>
            <TermsAndConditionsFormField />
          </If>

          <div
            className={
              passkeyAction
                ? 'grid grid-cols-1 items-start gap-3 sm:grid-cols-2'
                : 'grid grid-cols-1'
            }
          >
            <Button
              className={'h-11 w-full rounded-lg text-base'}
              data-test={'email-otp-send-button'}
              disabled={loading}
            >
              <If
                condition={loading}
                fallback={
                  <Trans
                    i18nKey={
                      passkeyAction ? 'common:continue' : 'auth:sendEmailCode'
                    }
                  />
                }
              >
                <Trans i18nKey={'auth:sendingEmailCode'} />
              </If>
            </Button>

            {passkeyAction}
          </div>
        </form>
      </Form>
    </>
  );
}

function VerifiedAlert() {
  return (
    <Alert variant={'success'}>
      <CheckIcon className={'h-4'} />

      <AlertTitle>
        <Trans i18nKey={'auth:verifyEmailCodeSuccess'} />
      </AlertTitle>

      <AlertDescription>
        <Trans i18nKey={'auth:verifyEmailCodeSuccessDescription'} />
      </AlertDescription>
    </Alert>
  );
}

function getEmailRedirectTo({
  redirectUrl,
  inviteToken,
}: {
  redirectUrl: string;
  inviteToken?: string;
}) {
  const url = new URL(redirectUrl, window.location.origin);

  if (inviteToken) {
    url.searchParams.set('invite_token', inviteToken);
  }

  return url.href;
}
